import { inject, Injectable } from '@angular/core';
import { ActionDisabledCondition, ActionItemOptions, UserAction } from '@mediahaven/shared';
import { ApiFragment, ApplicationConfigService, IsAccessibleInViewer, NavigationLink } from '@stylehaven/components';
import { Observable, of } from 'rxjs';
import { MediaHavenActionType } from '../../../entities/action-types.entity';
import { ActionLinkService } from '../../abstract/action-link.service.interface';

@Injectable({ providedIn: 'root' })
export class OpenInViewerActionService extends ActionLinkService {
    protected action: MediaHavenActionType = MediaHavenActionType.OPEN_IN_VIEWER;

    // Services
    private readonly configService: ApplicationConfigService = inject(ApplicationConfigService);

    // Properties
    private readonly baseUrl: string;

    constructor() {
        super();

        const baseUrl: string | undefined = this.configService.getInstant('baseUrl');

        if (!baseUrl) {
            console.warn('OpenInViewerActionService:: baseUrl is not configured');
            return;
        }

        this.baseUrl = baseUrl;
    }

    /*  Open in Viewer
     *  Available when:
     *   * item is NOT deleted
     *   * item is an access representation
     *
     *  Disabled when:
     *   * Never
     */
    public getUserAction(record: ApiFragment): Observable<UserAction> {
        return of(this.userActionBuilder(record));
    }

    public override userActionBuilder(record: ApiFragment): UserAction {
        const isDeleted: boolean = record.isDeleted();
        const isAccessibleInViewer: boolean = IsAccessibleInViewer(record);

        const availableConditions: boolean[] = [!isDeleted, isAccessibleInViewer, !!this.baseUrl];
        const disableConditions: ActionDisabledCondition[] = [];

        return new UserAction(availableConditions, disableConditions);
    }

    public getLabelKey(target: ApiFragment): string {
        return 'actions.open-in-viewer';
    }

    protected getLink(record: ApiFragment, actionItemOptions: ActionItemOptions): Observable<NavigationLink> {
        const urlParts: string[] = [this.baseUrl];
        if (!this.baseUrl.endsWith('/')) {
            urlParts.push('/');
        }
        if (actionItemOptions.shareContext) {
            urlParts.push('shared/', actionItemOptions.shareContext, '/');
        }
        urlParts.push('viewer/', record.getFragmentId());

        const externalUrl: string = urlParts.join('');

        return of({
            route: externalUrl,
            target: '_blank'
        });
    }
}
